/**
 * IFRAP-AI - Page thématique dynamique
 * Affiche le contenu IFRAP par section (santé, retraites, éducation, etc.)
 * Résout les 404 sur les URLs /sante, /retraites, /education, etc.
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink, TrendingUp, BookOpen, Newspaper, ChevronRight } from 'lucide-react';
import api from '../../api/client';

const THEMES = {
  sante: {
    title: 'Santé',
    subtitle: 'Dépenses de santé, hôpitaux, Sécurité sociale',
    color: '#E1000F',
    gradient: 'from-red-700 to-red-900',
    ifrapUrl: 'https://www.ifrap.org/emploi-et-politiques-sociales',
    category: 'sante',
    heroStat: { label: 'Déficit Sécu', value: '-23', unit: 'Mds€', source: 'PLFSS 2026' },
    relatedKpis: [
      { label: 'Dépenses de santé', value: '12,4', unit: '% PIB', source: 'OCDE 2024', comparison: 'vs 11,7% Allemagne' },
      { label: 'Déficit hospitalier', value: '-3,1', unit: 'Mds€', source: 'DGOS 2024', comparison: 'En dégradation' },
      { label: 'Reste à charge', value: '8,7', unit: '%', source: 'DREES 2024', comparison: 'Le plus bas d\'Europe' },
    ],
    keyPoints: [
      "La France dépense plus que ses voisins en santé publique, mais les résultats ne suivent pas",
      "Le déficit de la Sécurité sociale a doublé en deux ans pour atteindre -23 Mds€",
      "Les hôpitaux publics accumulent plus de 3 Mds€ de déficit annuel",
      "La France a 36,9 Mds€ de dépenses publiques de santé de plus que l'Allemagne",
    ],
    ifrapPosition: "La rationalisation des structures hospitalières et la responsabilisation des patients par le reste à charge sont des leviers majeurs pour réduire le déficit.",
    searchTerms: ['santé', 'hôpital', 'sécu', 'sécurité sociale', 'ALD', 'médecin'],
  },
  retraites: {
    title: 'Retraites',
    subtitle: 'Pensions, régimes spéciaux, COR',
    color: '#000091',
    gradient: 'from-blue-800 to-blue-950',
    ifrapUrl: 'https://www.ifrap.org/retraite',
    category: 'retraites',
    heroStat: { label: 'Retraites / PIB', value: '14,4', unit: '%', source: 'Eurostat 2024' },
    relatedKpis: [
      { label: 'Pensions fonctionnaires', value: '68', unit: 'Mds€/an', source: 'CAS Pensions 2025', comparison: 'Taux employeur 82%' },
      { label: 'Âge effectif de départ', value: '62,4', unit: 'ans', source: 'CNAV 2025', comparison: 'vs 64+ en Allemagne' },
      { label: 'Ratio cotisants/retraités', value: '1,7', unit: '', source: 'COR 2025', comparison: 'En baisse continue' },
    ],
    keyPoints: [
      "La France consacre 14,4% de son PIB aux retraites, contre 10% en Allemagne",
      "L'État cotise à 82,28% pour les pensions de ses fonctionnaires, vs 16,5% dans le privé",
      "Le rapport du COR projette un déficit structurel durable du système de retraites",
      "Les régimes spéciaux coûtent des milliards de subventions d'équilibre chaque année",
    ],
    ifrapPosition: "L'alignement des régimes public/privé et le recul de l'âge effectif de départ sont indispensables pour la soutenabilité du système.",
    searchTerms: ['retraite', 'pension', 'COR', 'fonctionnaire', 'régime spécial'],
  },
  education: {
    title: 'Éducation',
    subtitle: 'Enseignement, classements PISA, effectifs',
    color: '#059669',
    gradient: 'from-emerald-700 to-emerald-900',
    ifrapUrl: 'https://www.ifrap.org/education-et-culture',
    category: 'education',
    heroStat: { label: 'Dépense éducation', value: '5,5', unit: '% PIB', source: 'OCDE 2024' },
    relatedKpis: [
      { label: 'Agents Éducation nationale', value: '1,2', unit: 'million', source: 'DGAFP 2025', comparison: '1er employeur de France' },
      { label: 'Classement PISA (Maths)', value: '26ème', unit: '', source: 'OCDE PISA 2022', comparison: 'Sur 38 pays OCDE' },
      { label: 'Coût par élève (secondaire)', value: '11 300', unit: '€/an', source: 'DEPP 2024', comparison: 'vs 9 600€ en Allemagne' },
    ],
    keyPoints: [
      "Moins d'élèves devrait signifier moins de professeurs, mais les effectifs ne baissent pas",
      "La France dépense plus par élève que l'Allemagne avec des résultats PISA inférieurs",
      "326 000 agents publics supplémentaires depuis 2017, dont une part significative dans l'Éducation",
      "Le privé sous contrat obtient de meilleurs résultats avec moins de moyens par élève",
    ],
    ifrapPosition: "L'autonomie des établissements, la rémunération au mérite des enseignants et l'adaptation des effectifs à la démographie sont les clés d'une éducation plus performante.",
    searchTerms: ['éducation', 'école', 'prof', 'enseignant', 'PISA', 'élève'],
  },
  energie: {
    title: 'Énergie',
    subtitle: 'Transition énergétique, nucléaire, ENR',
    color: '#D97706',
    gradient: 'from-amber-700 to-amber-900',
    ifrapUrl: 'https://www.ifrap.org/agriculture-et-energie',
    category: 'energie',
    heroStat: { label: 'Coût des ENR', value: '7', unit: 'Mds€/an', source: 'CRE 2025' },
    relatedKpis: [
      { label: 'Part nucléaire', value: '72,5', unit: '%', source: 'RTE 2025', comparison: 'Production électrique' },
      { label: 'Prix électricité', value: '65', unit: '€/MWh', source: 'Eurostat T4 2025', comparison: 'En baisse' },
      { label: 'Émissions CO2 / PIB', value: '0,13', unit: 'kg/€', source: 'Eurostat 2024', comparison: 'Grâce au nucléaire' },
    ],
    keyPoints: [
      "La PPE3 (Programmation Pluriannuelle de l'Énergie) engage des dépenses considérables pour les ENR",
      "Le nucléaire assure 72,5% de la production électrique française avec un bilan carbone excellent",
      "Les engagements sur les ENR coûtent 7 Mds€ par an aux contribuables",
      "Le parc nucléaire français est un atout compétitif majeur face à l'Allemagne",
    ],
    ifrapPosition: "Le nucléaire est l'atout stratégique de la France. Les subventions massives aux ENR intermittentes sont un gaspillage qu'il faut réévaluer.",
    searchTerms: ['énergie', 'nucléaire', 'ENR', 'électricité', 'PPE', 'carbone'],
  },
  collectivites: {
    title: 'Collectivités',
    subtitle: 'Communes, départements, régions',
    color: '#7C3AED',
    gradient: 'from-violet-700 to-violet-900',
    ifrapUrl: 'https://www.ifrap.org/etat-et-collectivites',
    category: 'collectivites',
    heroStat: { label: 'Dépenses locales', value: '290', unit: 'Mds€', source: 'DGCL 2025' },
    relatedKpis: [
      { label: 'Agents territoriaux', value: '1,9', unit: 'million', source: 'DGAFP 2025', comparison: 'En hausse continue' },
      { label: 'Absentéisme territorial', value: '12,6', unit: 'jours/an', source: 'Sofaxis 2024', comparison: 'vs 7,9j dans le privé' },
      { label: 'Communes', value: '34 945', unit: '', source: 'DGCL 2025', comparison: 'Plus que toute l\'UE réunie' },
    ],
    keyPoints: [
      "La France compte plus de communes que tous les autres pays de l'UE réunis",
      "L'absentéisme dans la fonction publique territoriale est 60% supérieur au privé",
      "Les dotations de l'État aux collectivités sont anachroniques et mal réparties",
      "Le palmarès IFRAP des villes les mieux gérées révèle des écarts considérables",
    ],
    ifrapPosition: "La fusion des communes, la réduction du mille-feuille territorial et la transparence de la gestion locale sont des réformes urgentes.",
    searchTerms: ['commune', 'collectivité', 'département', 'région', 'maire', 'territorial'],
  },
  emploi: {
    title: 'Emploi',
    subtitle: 'Chômage, marché du travail, temps de travail',
    color: '#059669',
    gradient: 'from-teal-700 to-teal-900',
    ifrapUrl: 'https://www.ifrap.org/emploi-et-politiques-sociales',
    category: 'social',
    heroStat: { label: 'Taux de chômage', value: '7,9', unit: '%', source: 'INSEE T4 2025' },
    relatedKpis: [
      { label: 'Taux d\'emploi 20-64', value: '75,1', unit: '%', source: 'Eurostat 2024', comparison: 'vs 81% Allemagne' },
      { label: 'Allocataires RSA', value: '1,9', unit: 'million', source: 'CAF 2024', comparison: 'En hausse' },
      { label: 'Heures travaillées/an', value: '1 607', unit: 'h', source: 'OCDE 2023', comparison: 'vs 1 349h en Allemagne' },
    ],
    keyPoints: [
      "Le chômage repart à la hausse à 7,9% au T4 2025, +0,6 point en un an",
      "Les salariés français travaillent moins que leurs homologues européens",
      "1,9 million de personnes sont allocataires du RSA, un chiffre en hausse",
      "La fraude sociale ne rapporte que 1,2 Md€ recouvré alors que son ampleur est estimée à plus de 10 Mds€",
    ],
    ifrapPosition: "Réformer le marché du travail, simplifier le Code du travail et conditionner le RSA à une activité sont les clés du plein emploi.",
    searchTerms: ['emploi', 'chômage', 'RSA', 'travail', 'CDI', 'heures'],
  },
  finances: {
    title: 'Finances publiques',
    subtitle: 'Budget, dette, fiscalité',
    color: '#E1000F',
    gradient: 'from-red-800 to-red-950',
    ifrapUrl: 'https://www.ifrap.org/budget-et-fiscalite',
    category: 'finances_publiques',
    heroStat: { label: 'Dette publique', value: '115,6', unit: '% PIB', source: 'INSEE T4 2025' },
    relatedKpis: [
      { label: 'Déficit public', value: '-5,1', unit: '% PIB', source: 'INSEE 2025 (définitif)', comparison: 'Hors critères UE' },
      { label: 'Dépenses publiques', value: '58,2', unit: '% PIB', source: 'INSEE 2025 (définitif)', comparison: 'Record européen' },
      { label: 'Prélèvements obligatoires', value: '43,6', unit: '% PIB', source: 'INSEE 2025 (définitif)', comparison: 'Record OCDE' },
    ],
    keyPoints: [
      "50 ans de déficits ininterrompus depuis 1974",
      "La France taxe et dépense plus que tous ses voisins européens",
      "Le déficit 2025 à 5,1% est une fausse bonne nouvelle : les dépenses n'ont pas baissé",
      "La charge de la dette atteint 60 Mds€/an, bientôt le premier poste budgétaire",
    ],
    ifrapPosition: "Sans réduction structurelle des dépenses publiques de 5 points de PIB, la trajectoire de dette est insoutenable.",
    searchTerms: ['budget', 'dette', 'déficit', 'fiscal', 'impôt', 'dépense publique'],
  },
};

const ThemePage = () => {
  const { theme: themeSlug } = useParams();
  const theme = THEMES[themeSlug];
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!theme) return;
    const fetchArticles = async () => {
      try {
        const { data } = await api.get('/api/publications/public', {
          params: { category: theme.category, limit: 6 },
        });
        setArticles(data.publications || []);
      } catch {
        setArticles([]);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, [theme]);

  if (!theme) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-white" data-testid={`theme-page-${themeSlug}`}>
      {/* Hero */}
      <div className={`bg-gradient-to-br ${theme.gradient} text-white`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-14">
          <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Observatoire
          </Link>
          <h1 className="text-3xl sm:text-5xl font-bold mb-3">{theme.title}</h1>
          <p className="text-lg text-white/80 mb-8">{theme.subtitle}</p>

          {/* Hero stat */}
          <div className="inline-flex items-baseline gap-3 bg-white/10 backdrop-blur rounded-xl px-6 py-4">
            <span className="text-4xl sm:text-5xl font-bold">{theme.heroStat.value}</span>
            <span className="text-xl text-white/80">{theme.heroStat.unit}</span>
            <span className="text-sm text-white/60 ml-2">{theme.heroStat.label} · {theme.heroStat.source}</span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        {/* KPI Cards */}
        <section className="mb-12">
          <h2 className="text-xl font-bold text-gray-900 mb-5 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" style={{ color: theme.color }} />
            Indicateurs clés
          </h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {theme.relatedKpis.map((kpi, i) => (
              <div key={i} className="bg-gray-50 rounded-xl p-5 border border-gray-100">
                <p className="text-sm text-gray-500 mb-1">{kpi.label}</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold" style={{ color: theme.color }}>{kpi.value}</span>
                  <span className="text-sm text-gray-500">{kpi.unit}</span>
                </div>
                <p className="text-xs text-gray-400 mt-2">{kpi.comparison}</p>
                <p className="text-xs text-gray-300 mt-1">{kpi.source}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Key Points */}
        <section className="mb-12">
          <h2 className="text-xl font-bold text-gray-900 mb-5 flex items-center gap-2">
            <BookOpen className="w-5 h-5" style={{ color: theme.color }} />
            Ce qu'il faut retenir
          </h2>
          <div className="space-y-3">
            {theme.keyPoints.map((point, i) => (
              <div key={i} className="flex gap-3 items-start">
                <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0 mt-0.5" style={{ backgroundColor: theme.color }}>
                  {i + 1}
                </div>
                <p className="text-gray-700 leading-relaxed">{point}</p>
              </div>
            ))}
          </div>

          {/* IFRAP Position */}
          <div className="mt-6 border-l-4 pl-5 py-3" style={{ borderColor: theme.color }}>
            <p className="text-sm font-semibold text-gray-500 mb-1">Position IFRAP</p>
            <p className="text-gray-800 italic">{theme.ifrapPosition}</p>
          </div>
        </section>

        {/* Publications */}
        <section className="mb-12">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Newspaper className="w-5 h-5" style={{ color: theme.color }} />
              Publications IFRAP
            </h2>
            <Link
              to={`/publications?category=${theme.category}`}
              className="text-sm font-medium flex items-center gap-1 hover:underline"
              style={{ color: theme.color }}
            >
              Voir tout <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-32 bg-gray-100 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : articles.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {articles.map(pub => (
                <Link
                  key={pub.id || pub.title}
                  to={`/publications/${pub.id}`}
                  className="bg-gray-50 rounded-xl p-4 border border-gray-100 hover:border-gray-300 transition-colors group"
                >
                  <p className="text-sm font-semibold text-gray-900 group-hover:underline line-clamp-2">{pub.title}</p>
                  <p className="text-xs text-gray-500 mt-2 line-clamp-2">{pub.summary || pub.description}</p>
                  <p className="text-xs text-gray-400 mt-2">{pub.date}</p>
                </Link>
              ))}
            </div>
          ) : (
            <div className="bg-gray-50 rounded-xl p-8 text-center">
              <p className="text-gray-500 text-sm">Pas encore de publications dans cette catégorie.</p>
              <a
                href={theme.ifrapUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 mt-3 text-sm font-medium hover:underline"
                style={{ color: theme.color }}
              >
                Voir sur ifrap.org <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          )}
        </section>

        {/* Link to ifrap.org */}
        <section className="text-center py-8 border-t border-gray-100">
          <p className="text-gray-500 text-sm mb-3">Retrouvez toutes les analyses IFRAP sur ce thème</p>
          <a
            href={theme.ifrapUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-white px-6 py-3 rounded-lg font-medium text-sm hover:opacity-90 transition-opacity"
            style={{ backgroundColor: theme.color }}
            data-testid="ifrap-org-link"
          >
            Consulter sur ifrap.org <ExternalLink className="w-4 h-4" />
          </a>
        </section>
      </div>
    </div>
  );
};

export { THEMES };
export default ThemePage;
